/**
 * Find an existing session worktree layout on disk under the worktrees root (CLI restarts / legacy meta).
 *
 * New layout: `{worktreesRoot}/{bridgeKey}/{sessionId}/{repoRel…}` (each repo dir is a git worktree).
 * Legacy: `{worktreesRoot}/{bridgeKey}/{repoRel}/{sessionId}` (directory named `sessionId` is the checkout).
 *
 * Directory walks are async and yield periodically so large worktrees trees do not block the bridge event loop.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { isCliImmediateShutdownRequested } from '../../runtime/cli-process-interrupt.js';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { resolveIsolatedSessionParentPathFromCheckouts } from '../../paths/session-layout-paths.js';
import type { WorktreeLayoutFile } from '../worktree-layout-file.js';
import { getLauncherDirNameIfPresent } from '../worktree-layout-file.js';
import type { SessionWorktreeBinding } from '../session-worktree-binding.js';
import { collectWorktreeRootsNamed } from './collect-worktree-paths.js';
import type { DiskWalkState } from './disk-walk-constants.js';
import { yieldDuringDiskWalk } from './disk-walk-utils.js';
import { layoutKeysForBridgeRoot } from './layout-keys-for-bridge-root.js';
import { tryBindingFromSessionDirectory } from './try-binding-from-session-directory.js';

export async function discoverSessionWorktreeOnDisk(options: {
  sessionId: string;
  worktreesRootPath: string;
  layout: WorktreeLayoutFile;
  bridgeRoot: string;
}): Promise<SessionWorktreeBinding | null> {
  const { sessionId, worktreesRootPath, layout, bridgeRoot } = options;
  const sid = sessionId.trim();
  if (!sid) return null;
  try {
    await fs.promises.access(worktreesRootPath);
  } catch {
    return null;
  }

  const preferredKey = getLauncherDirNameIfPresent(layout, bridgeRoot);
  const keysOnDisk: string[] = [];
  if (preferredKey) keysOnDisk.push(preferredKey);
  const scanState: DiskWalkState = { entries: 0 };
  try {
    for (const name of await fs.promises.readdir(worktreesRootPath)) {
      if (!(await yieldDuringDiskWalk(scanState))) break;
      if (name.startsWith('.')) continue;
      const p = path.join(worktreesRootPath, name);
      let st: fs.Stats;
      try {
        st = await fs.promises.stat(p);
      } catch {
        continue;
      }
      if (!st.isDirectory()) continue;
      if (!keysOnDisk.includes(name)) keysOnDisk.push(name);
    }
  } catch {
    return null;
  }

  const keys = layoutKeysForBridgeRoot(layout, bridgeRoot, keysOnDisk);

  for (const key of keys) {
    if (isCliImmediateShutdownRequested()) return null;
    await yieldToEventLoop();
    const layoutRoot = path.join(worktreesRootPath, key);
    let layoutSt: fs.Stats;
    try {
      layoutSt = await fs.promises.stat(layoutRoot);
    } catch {
      continue;
    }
    if (!layoutSt.isDirectory()) continue;

    const sessionDir = path.join(layoutRoot, sid);
    const nested = await tryBindingFromSessionDirectory(sessionDir);
    if (nested) return nested;

    const legacy = await collectWorktreeRootsNamed(layoutRoot, sid);
    if (legacy.paths.length > 0) {
      const isolated =
        resolveIsolatedSessionParentPathFromCheckouts(legacy.paths) ?? path.resolve(legacy.paths[0]!);
      return {
        sessionParentPath: path.resolve(isolated),
        repoCheckoutPaths: legacy.paths.map((p) => path.resolve(p)),
      };
    }
  }
  return null;
}
